import { ReactNode, useEffect, useState } from 'react';
import classNames from 'classnames';
import { UniversalTrackerValueView } from '@features/universal-tracker-value';
import { ReadMoreButton } from '@g17eco/molecules/read-more-button';
import { useContentOverflow } from '@hooks/useContentOverflow';
import { UtrValueType } from '@g17eco/types/universalTracker';
import { UniversalTrackerModalServiceUtrv } from '@g17eco/types/universalTrackerValue';
import UniversalTrackerClassInterface from '../../model/UniversalTrackerClassInterface';

export interface HistoryValueProps {
  utr: UniversalTrackerClassInterface;
  utrv: UniversalTrackerModalServiceUtrv;
  selectedColumnCode?: string;
  showFullAnswer?: boolean;
  onClampedChange?: (utrvId: string, isClamped: boolean) => void;
}

// Answers that can run past two lines; numbers and tables keep the one-line view
const EXPANDABLE_TYPES: string[] = [UtrValueType.Text, UtrValueType.ValueList, UtrValueType.ValueListMulti];

interface ExpandableAnswerProps extends Pick<HistoryValueProps, 'showFullAnswer' | 'onClampedChange'> {
  utrvId: string;
  aggregatedMark: string;
  hasBullets: boolean;
  children: ReactNode;
}

const ExpandableAnswer = (props: ExpandableAnswerProps) => {
  const { utrvId, aggregatedMark, hasBullets, showFullAnswer = false, onClampedChange, children } = props;
  const [isRowExpanded, setIsRowExpanded] = useState(false);
  const isExpanded = showFullAnswer || isRowExpanded;
  // An expanded answer never overflows, so keep the clamped result or Read less would disappear
  const { containerRef, isOverflowing } = useContentOverflow({ enabled: !isExpanded });

  useEffect(() => {
    onClampedChange?.(utrvId, isOverflowing);
    return () => onClampedChange?.(utrvId, false);
  }, [utrvId, isOverflowing, onClampedChange]);

  return (
    <div className='history-value text-left'>
      <div className='d-flex'>
        <div
          ref={containerRef}
          className={classNames('history-value__text', {
            'history-value__text--clamped': !isExpanded,
            'history-value__text--bullets': hasBullets,
          })}
        >
          {children}
        </div>
        {aggregatedMark ? <span className='align-self-end'>{aggregatedMark}</span> : null}
      </div>
      {isOverflowing && !showFullAnswer ? (
        <ReadMoreButton isHidden={!isRowExpanded} onToggle={() => setIsRowExpanded((prev) => !prev)} className='p-0' />
      ) : null}
    </div>
  );
};

export const HistoryValue = ({ utr, utrv, selectedColumnCode, showFullAnswer, onClampedChange }: HistoryValueProps) => {
  const valueType = utr.getValueType();
  const isExpandable = EXPANDABLE_TYPES.includes(valueType);
  const aggregatedMark = utrv.aggregationCount ? '*' : '';

  const value = (
    <UniversalTrackerValueView
      utr={utr.getRaw()}
      utrv={utrv}
      valueType={valueType}
      showTableDownload={false}
      extractColumnCode={selectedColumnCode}
      classes={{ popover: undefined }}
      unitConfig={utrv.surveyUnitConfig}
      display={isExpandable ? 'normal' : 'minimal'}
    />
  );

  if (!isExpandable) {
    return (
      <div className='text-truncate'>
        {value}
        {aggregatedMark ? <span>{aggregatedMark}</span> : null}
      </div>
    );
  }

  return (
    <ExpandableAnswer
      utrvId={utrv._id}
      aggregatedMark={aggregatedMark}
      hasBullets={valueType === UtrValueType.ValueListMulti}
      showFullAnswer={showFullAnswer}
      onClampedChange={onClampedChange}
    >
      {value}
    </ExpandableAnswer>
  );
};
